import * as z$1 from "zod/v4";
//#region src/schemas/runtime/local-agent-catalog.ts
const LOCAL_AGENT_CATALOG_BOUNDED_ID_SCHEMA = z$1.string().trim().min(1).max(256);
const LOCAL_AGENT_CATALOG_BOUNDED_LABEL_SCHEMA = z$1.string().trim().min(1).max(256);
const localAgentCatalogTargetKindSchema = z$1.enum(["builtin", "custom"]);
const localAgentCatalogLifecycleStatusSchema = z$1.enum(["active", "suspended"]);
const localAgentCatalogSelectableReasonSchema = z$1.enum([
	"not_detected",
	"not_supported_yet",
	"needs_repair",
	"custom_coming_soon"
]);
const localAgentCatalogLaunchStatusSchema = z$1.enum([
	"ready",
	"needs_choice",
	"needs_repair",
	"not_available"
]);
const localAgentCatalogEntryShape = {
	agentId: LOCAL_AGENT_CATALOG_BOUNDED_ID_SCHEMA,
	displayName: LOCAL_AGENT_CATALOG_BOUNDED_LABEL_SCHEMA,
	targetKind: localAgentCatalogTargetKindSchema,
	detected: z$1.boolean(),
	enabled: z$1.boolean(),
	launchStatus: localAgentCatalogLaunchStatusSchema,
	selectable: z$1.boolean(),
	selectableReason: z$1.union([localAgentCatalogSelectableReasonSchema, z$1.null()])
};
const localAgentCatalogEntrySchema = z$1.strictObject(localAgentCatalogEntryShape).superRefine(validateLocalAgentCatalogEntrySemantics);
const localAgentCatalogEntryWithLifecycleSchema = z$1.strictObject({
	...localAgentCatalogEntryShape,
	lifecycleStatus: localAgentCatalogLifecycleStatusSchema.default("active")
}).superRefine(validateLocalAgentCatalogEntrySemantics);
const localAgentCatalogSchema = createLocalAgentCatalogSchema(localAgentCatalogEntrySchema);
const localAgentCatalogWithLifecycleSchema = createLocalAgentCatalogSchema(localAgentCatalogEntryWithLifecycleSchema);
function createLocalAgentCatalogSchema(entrySchema) {
	return z$1.strictObject({
		totalBuiltins: z$1.number().int().min(0),
		detectedSupported: z$1.number().int().min(0),
		needsChoice: z$1.number().int().min(0),
		needsRepair: z$1.number().int().min(0),
		detectedNotSupportedYet: z$1.number().int().min(0),
		notDetected: z$1.number().int().min(0),
		entries: z$1.array(entrySchema)
	}).superRefine(validateLocalAgentCatalogSummary);
}
function validateLocalAgentCatalogEntrySemantics(value, ctx) {
	if (value.selectable && value.selectableReason !== null) ctx.addIssue({
		code: "custom",
		message: "selectableReason must be null when selectable is true",
		path: ["selectableReason"]
	});
	if (!value.selectable && value.selectableReason === null) ctx.addIssue({
		code: "custom",
		message: "selectableReason must be present when selectable is false",
		path: ["selectableReason"]
	});
	if (value.launchStatus === "needs_choice" && !(value.detected && value.targetKind === "builtin" && value.selectable)) ctx.addIssue({
		code: "custom",
		message: "needs_choice launchStatus only applies to detected selectable builtin entries",
		path: ["launchStatus"]
	});
	if (value.launchStatus === "needs_repair" && !(value.detected && value.enabled && value.targetKind === "builtin" && value.selectable && value.selectableReason === null)) ctx.addIssue({
		code: "custom",
		message: "needs_repair launchStatus only applies to detected selectable builtin entries",
		path: ["launchStatus"]
	});
	if (value.selectableReason === "not_detected" && value.detected) ctx.addIssue({
		code: "custom",
		message: "not_detected selectableReason requires detected to be false",
		path: ["detected"]
	});
	if (value.selectableReason === "not_supported_yet" && (!value.detected || value.targetKind !== "builtin")) ctx.addIssue({
		code: "custom",
		message: "not_supported_yet selectableReason only applies to detected builtin entries",
		path: ["selectableReason"]
	});
	if (value.selectableReason === "needs_repair" && (!value.detected || value.targetKind !== "builtin")) ctx.addIssue({
		code: "custom",
		message: "needs_repair selectableReason only applies to detected builtin entries",
		path: ["selectableReason"]
	});
	if (value.selectableReason === "custom_coming_soon" && value.targetKind !== "custom") ctx.addIssue({
		code: "custom",
		message: "custom_coming_soon selectableReason only applies to custom entries",
		path: ["selectableReason"]
	});
}
function validateLocalAgentCatalogSummary(value, ctx) {
	const builtinEntries = value.entries.filter((entry) => entry.targetKind === "builtin");
	const detectedSupported = builtinEntries.filter((entry) => entry.detected && entry.launchStatus === "ready").length;
	const needsChoice = builtinEntries.filter((entry) => entry.detected && entry.launchStatus === "needs_choice").length;
	const detectedNotSupportedYet = builtinEntries.filter((entry) => entry.detected && entry.launchStatus === "not_available" && entry.selectableReason === "not_supported_yet").length;
	const needsRepair = builtinEntries.filter((entry) => entry.detected && entry.launchStatus === "needs_repair").length;
	const notDetected = builtinEntries.filter((entry) => !entry.detected).length;
	const expectations = [
		{
			actual: builtinEntries.length,
			expected: value.totalBuiltins,
			label: "totalBuiltins",
			path: ["totalBuiltins"]
		},
		{
			actual: detectedSupported,
			expected: value.detectedSupported,
			label: "detectedSupported",
			path: ["detectedSupported"]
		},
		{
			actual: needsChoice,
			expected: value.needsChoice,
			label: "needsChoice",
			path: ["needsChoice"]
		},
		{
			actual: detectedNotSupportedYet,
			expected: value.detectedNotSupportedYet,
			label: "detectedNotSupportedYet",
			path: ["detectedNotSupportedYet"]
		},
		{
			actual: needsRepair,
			expected: value.needsRepair,
			label: "needsRepair",
			path: ["needsRepair"]
		},
		{
			actual: notDetected,
			expected: value.notDetected,
			label: "notDetected",
			path: ["notDetected"]
		}
	];
	for (const expectation of expectations) {
		if (expectation.actual === expectation.expected) continue;
		ctx.addIssue({
			code: "custom",
			message: `${expectation.label} must match the builtin entry summary`,
			path: expectation.path
		});
	}
}
//#endregion
export { localAgentCatalogEntrySchema, localAgentCatalogEntryWithLifecycleSchema, localAgentCatalogLaunchStatusSchema, localAgentCatalogLifecycleStatusSchema, localAgentCatalogSchema, localAgentCatalogSelectableReasonSchema, localAgentCatalogTargetKindSchema, localAgentCatalogWithLifecycleSchema };

//# sourceMappingURL=local-agent-catalog.js.map